import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { Helmet } from "react-helmet";
import NavigationBar from "../../components/Heritage/NavigationBar";
import anarkaliData from "../../components/Data/anarkali-varieties.json";

// Category order mirrors the section order used in the JSON file.
// (Anarkali varieties group naturally by silhouette/fabric/neckline/region
// rather than by geographic zone, so this replaces the North/South/
// East/West grouping used on the main Fashion page.)
const CATEGORY_ORDER = [
  "Silhouette & Length",
  "Fabric & Craft Tradition",
  "Neckline & Sleeve Style",
  "Regional & Bridal Variants",
];

// Same deterministic masonry tile cycle used on Fashion.jsx / Kanjeevaram.jsx
const TILE_VARIANTS = ["normal", "wide", "tall", "large"];
const getTileVariant = (index) => TILE_VARIANTS[index % TILE_VARIANTS.length];

const AnarkaliCard = ({ item, index, onSelect }) => (
  <button
    type="button"
    className={`kj-card kj-tile-${getTileVariant(index)}`}
    onClick={() => onSelect(item)}
  >
    <div className="kj-card-img-wrap">
      <img
        src={item.image}
        alt={item.name}
        loading="lazy"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src =
            "https://commons.wikimedia.org/wiki/Special:FilePath/Anarkali suit.jpg";
        }}
      />
    </div>
    <div className="kj-card-body">
      <span className="kj-card-meta">{item.category}</span>
      <h3 className="kj-card-title">{item.name}</h3>
      <p className="kj-card-desc">{item.description}</p>
    </div>
  </button>
);

const AnarkaliModal = ({ item, onClose }) => {
  if (!item) return null;
  const standardFields = new Set([
    "name",
    "category",
    "region",
    "occasion",
    "description",
    "image",
    "materials",
    "difficulty",
    "notes",
  ]);
  const extraFields = Object.entries(item).filter(
    ([key, val]) => !standardFields.has(key) && val
  );

  return (
    <Modal
      show={!!item}
      onHide={onClose}
      centered
      size="lg"
      className="kj-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{item.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="kj-modal-content-wrap">
          <div className="kj-modal-img">
            <img
              src={item.image}
              alt={item.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://commons.wikimedia.org/wiki/Special:FilePath/Anarkali suit.jpg";
              }}
            />
          </div>
          <div className="kj-modal-details">
            <p className="kj-modal-desc">{item.description}</p>
            <ul className="kj-modal-facts">
              <li>
                <strong>Category:</strong> {item.category}
              </li>
              <li>
                <strong>Region:</strong> {item.region}
              </li>
              <li>
                <strong>Occasion:</strong> {item.occasion}
              </li>
              <li>
                <strong>Materials:</strong> {item.materials}
              </li>
              <li>
                <strong>Weave difficulty:</strong> {item.difficulty}
              </li>
            </ul>
            {item.notes && (
              <p className="kj-modal-notes">
                <strong>Note:</strong> {item.notes}
              </p>
            )}
            {extraFields.length > 0 && (
              <ul className="kj-modal-facts kj-modal-extra">
                {extraFields.map(([key, val]) => (
                  <li key={key}>
                    <strong>{key}:</strong> {String(val)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const CategoryFilter = ({ active, onChange }) => (
  <div className="kj-filter-bar">
    <button
      className={`kj-filter-pill ${active === "All" ? "is-active" : ""}`}
      onClick={() => onChange("All")}
    >
      All
    </button>
    {CATEGORY_ORDER.map((cat) => (
      <button
        key={cat}
        className={`kj-filter-pill ${active === cat ? "is-active" : ""}`}
        onClick={() => onChange(cat)}
      >
        {cat}
      </button>
    ))}
  </div>
);

const AnarkaliSuit = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [selected, setSelected] = useState(null);

  const sections = useMemo(() => {
    return CATEGORY_ORDER.map((cat) => ({
      category: cat,
      items: anarkaliData[cat] || [],
    })).filter(
      (section) =>
        activeCategory === "All" || section.category === activeCategory
    );
  }, [activeCategory]);

  const totalCount = useMemo(
    () => Object.values(anarkaliData).reduce((sum, arr) => sum + arr.length, 0),
    []
  );

  return (
    <div className="kj-page">
      <style>{`
/* ===== Kanjeevaram page — design system ===== */
.kj-page {
  --kj-ink: #1c1a17;
  --kj-ink-soft: #4a463f;
  --kj-gray-100: #f7f6f3;
  --kj-gray-200: #eeece6;
  --kj-gray-300: #ded9cf;
  --kj-brass: #a97c3d;
  --kj-brass-dark: #7c5a2a;
  --kj-white: #ffffff;
  --kj-radius: 10px;
  --kj-shadow: 0 6px 24px rgba(28, 26, 23, 0.08);
  --kj-shadow-hover: 0 14px 34px rgba(28, 26, 23, 0.14);

  background: var(--kj-white);
  color: var(--kj-ink);
  font-family: "Work Sans", -apple-system, BlinkMacSystemFont, sans-serif;
}

.kj-page h1,
.kj-page h2,
.kj-page h3 {
  font-family: "Fraunces", Georgia, serif;
  font-weight: 600;
  letter-spacing: -0.01em;
}

/* ===== Hero ===== */
.kj-hero {
  background: linear-gradient(180deg, var(--kj-gray-100) 0%, var(--kj-white) 100%);
  border-bottom: 1px solid var(--kj-gray-300);
  padding: 64px 24px 48px;
  text-align: center;
}

.kj-hero-inner {
  max-width: 720px;
  margin: 0 auto;
}

.kj-hero-badge {
  display: inline-block;
  font-family: "IBM Plex Mono", monospace;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--kj-brass-dark);
  background: rgba(169, 124, 61, 0.1);
  border: 1px solid rgba(169, 124, 61, 0.3);
  border-radius: 999px;
  padding: 6px 14px;
  margin-bottom: 18px;
}

.kj-hero h1 {
  font-size: clamp(32px, 5vw, 48px);
  margin: 0 0 14px;
}

.kj-hero p {
  font-size: 16px;
  line-height: 1.6;
  color: var(--kj-ink-soft);
  margin: 0;
}

/* ===== Filter bar ===== */
.kj-filter-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  padding: 28px 24px;
  border-bottom: 1px solid var(--kj-gray-200);
}

.kj-filter-pill {
  font-family: "IBM Plex Mono", monospace;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: var(--kj-white);
  color: var(--kj-ink-soft);
  border: 1px solid var(--kj-gray-300);
  border-radius: 999px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.kj-filter-pill:hover {
  border-color: var(--kj-brass);
  color: var(--kj-brass-dark);
}

.kj-filter-pill.is-active {
  background: var(--kj-ink);
  border-color: var(--kj-ink);
  color: var(--kj-white);
}

/* ===== Main / sections ===== */
.kj-main {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px 80px;
}

.kj-section {
  margin-bottom: 56px;
}

.kj-section-title {
  font-size: 24px;
  margin: 0 0 22px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--kj-brass);
  display: inline-block;
}

/* ===== Masonry grid ===== */
.kj-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  grid-auto-rows: 220px;
  gap: 20px;
}

.kj-tile-wide {
  grid-column: span 2;
}

.kj-tile-tall {
  grid-row: span 2;
}

.kj-tile-large {
  grid-column: span 2;
  grid-row: span 2;
}

/* ===== Card ===== */
.kj-card {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border: 1px solid var(--kj-gray-300);
  border-radius: var(--kj-radius);
  overflow: hidden;
  background: var(--kj-white);
  box-shadow: var(--kj-shadow);
  padding: 0;
  text-align: left;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.kj-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--kj-shadow-hover);
}

.kj-card-img-wrap {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  background: var(--kj-gray-200);
}

.kj-card-img-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.kj-card-img-wrap::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 45%, rgba(0, 0, 0, 0.72) 100%);
}

.kj-card-body {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 16px 18px;
  color: var(--kj-white);
  z-index: 2;
}

.kj-card-meta {
  display: block;
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #e8d9bf;
  margin-bottom: 4px;
}

.kj-card-title {
  font-size: 18px;
  line-height: 1.25;
  margin: 0 0 6px;
  color: var(--kj-white);
}

.kj-card-desc {
  font-size: 13px;
  line-height: 1.45;
  color: #e9e7e2;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@media (max-width: 640px) {
  .kj-tile-wide,
  .kj-tile-large {
    grid-column: span 1;
  }
  .kj-tile-tall,
  .kj-tile-large {
    grid-row: span 1;
  }
}

/* ===== Modal ===== */
.kj-modal .modal-content {
  border-radius: var(--kj-radius);
  border: none;
}

.kj-modal .modal-title {
  font-family: "Fraunces", Georgia, serif;
  font-weight: 600;
}

.kj-modal-content-wrap {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.kj-modal-img {
  width: 100%;
  border-radius: var(--kj-radius);
  overflow: hidden;
  background: var(--kj-gray-200);
}

.kj-modal-img img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

.kj-modal-details {
  flex: 1;
}

.kj-modal-desc {
  font-size: 15px;
  line-height: 1.6;
  color: var(--kj-ink-soft);
  margin-bottom: 16px;
}

.kj-modal-facts {
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.9;
}

.kj-modal-facts li strong {
  font-family: "IBM Plex Mono", monospace;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--kj-brass-dark);
  margin-right: 6px;
}

.kj-modal-notes {
  font-size: 14px;
  line-height: 1.6;
  background: var(--kj-gray-100);
  border-left: 3px solid var(--kj-brass);
  padding: 10px 14px;
  border-radius: 4px;
}

.kj-modal-extra {
  border-top: 1px solid var(--kj-gray-200);
  padding-top: 10px;
}

@media (min-width: 769px) {
  .kj-modal-content-wrap {
    flex-direction: row;
  }
  .kj-modal-img {
    flex: 0 0 42%;
    max-width: 380px;
  }
}

      `}</style>
      <Helmet>
        <title>Anarkali Suits: A Style-by-Style Guide | Bharat Abroad</title>
        <meta
          name="description"
          content="Explore the varieties of Anarkali suits — from floor-length bridal styles and Hyderabadi zardozi to Lucknowi chikankari and everyday Anarkali kurtis."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Anarkali Suit Varieties",
            numberOfItems: totalCount,
          })}
        </script>
      </Helmet>

      <NavigationBar />

      <header className="kj-hero">
        <div className="kj-hero-inner">
          <span className="kj-hero-badge">Textile Heritage</span>
          <h1>Anarkali Suits</h1>
          <p>
            {totalCount} varieties of the Anarkali silhouette — grouped by
            length, fabric and craft tradition, neckline style, and regional
            bridal variants.
          </p>
        </div>
      </header>

      <CategoryFilter active={activeCategory} onChange={setActiveCategory} />

      <main className="kj-main">
        {sections.map((section) => (
          <section key={section.category} className="kj-section">
            <h2 className="kj-section-title">{section.category}</h2>
            <div className="kj-grid">
              {section.items.map((item, idx) => (
                <AnarkaliCard
                  key={item.name}
                  item={item}
                  index={idx}
                  onSelect={setSelected}
                />
              ))}
            </div>
          </section>
        ))}
      </main>

      <AnarkaliModal item={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default AnarkaliSuit;
